import { useMemo, useState } from 'react'
import classes from './TeamComponents.module.css'
import clsx from 'clsx'
import { useDebouncedValue, useMediaQuery } from '@mantine/hooks'
import { IconZoom } from '@tabler/icons-react'
import { AspectRatio, Table, TextInput, Image } from '@mantine/core'
import { useNavigate, useParams } from 'react-router'
import { usePlayerRanks } from '../../ranks/api'
import { Typography } from '../../common/typography'
import { getRankSuffix } from '../../common/helpers'

export default function TeamPlayers() {
  const isDesktop = useMediaQuery('(min-width: 800px)')
  const [searchPlayer, setSearchPlayer] = useState<string>('')
  const [debounce] = useDebouncedValue(searchPlayer, 300)
  const { competitionId, teamId } = useParams()
  const navigate = useNavigate()

  const { data: playerRanksResponse } = usePlayerRanks({
    competition_id: Number(competitionId),
    team_id: Number(teamId),
  })

  const playerRanks = playerRanksResponse.items

  const filteredPlayers = useMemo(() => {
    return playerRanks.filter((player) =>
      player.player_name.toLowerCase().includes(debounce.toLowerCase())
    )
  }, [playerRanks, debounce])

  const handleRowClick = (id: number) => {
    navigate(`/competitions/${competitionId}/players/${id}`)
  }

  const rows = filteredPlayers.map((player, index) => {
    return (
      <Table.Tr
        key={player.player_id}
        onClick={() => handleRowClick(player.player_id)}
      >
        <Table.Td>
          <Typography lh="150" ta="center">
            {index + 1}
            <sup>{getRankSuffix(index + 1)}</sup>
          </Typography>
        </Table.Td>
        <Table.Td>
          <div className={classes.player_cell}>
            <AspectRatio ratio={1 / 1}>
              <Image
                src={player.player_photo.thumbnail}
                alt={player.player_name}
                className={clsx(classes.playerImage, {
                  [classes.playerImageDesktop]: isDesktop,
                })}
                fit="scale-down"
              />
            </AspectRatio>
            <Typography>{player.player_name}</Typography>
          </div>
        </Table.Td>

        <Table.Td>
          <Typography ff="JetBrains Mono" lh="140" ta="center">
            {player.rank}
            <sup>{getRankSuffix(player.rank)}</sup>
          </Typography>
        </Table.Td>

        <Table.Td>
          <Typography ff="JetBrains Mono" lh="140" ta="center">
            {player.rating.toFixed(1)}
          </Typography>
        </Table.Td>
      </Table.Tr>
    )
  })

  return (
    <div className={classes.players_container}>
      <TextInput
        placeholder="Search"
        rightSection={<IconZoom className={classes.select_icon} />}
        rightSectionPointerEvents="none"
        size="xs"
        className={clsx({ [classes.searchDesktop]: isDesktop })}
        classNames={{
          root: classes.search_root,
          input: classes.search_input,
        }}
        value={searchPlayer}
        onChange={(event) => setSearchPlayer(event.currentTarget.value)}
      />
      <div className={classes.tableContainer}>
        <Table.ScrollContainer minWidth={300}>
          <Table
            classNames={{
              table: classes.table,
              tr: classes.tr,
              th: classes.th,
              td: classes.td,
            }}
            verticalSpacing="xs"
            withRowBorders={false}
          >
            {!isDesktop && (
              <Table.Thead>
                <Table.Tr className={classes.headerRow}>
                  <Table.Th>#</Table.Th>
                  <Table.Th>Player</Table.Th>
                  <Table.Th>Rank</Table.Th>
                  <Table.Th>Rating</Table.Th>
                </Table.Tr>
              </Table.Thead>
            )}
            <Table.Tbody>{rows}</Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </div>
    </div>
  )
}
